// One shared copy of the subscription-flag response for the whole app.
//
// The guide wants subscription-flag checked on login and on every page
// change, and again after any payment action. Keeping the result in a tiny
// external store (read through useSyncExternalStore) means the route guard,
// the popup, the subscription header and the menus all read the same
// object instead of each firing its own request.
//
// Calls are kept to a minimum (each one costs API time and billing):
//   ensureSubscriptionFlag()  → route changes / screen mounts. Reuses the
//                               cached answer while it is still fresh:
//       paid / not applicable / exempt → until local midnight (the state
//                               only changes through a debit on a new day,
//                               or a payment action, which forces a call)
//       expired (not paid)     → re-checked at most once a minute
//       call failed            → retried after a minute (fail-open)
//   refreshSubscriptionFlag() → always calls. Only for moments the state is
//                               known to change: login (via
//                               clearSubscriptionFlag), Pay Now, AutoPay
//                               success, "Check again".
// A successful answer is also kept in sessionStorage, so a page refresh
// (F5) reuses it too. sessionStorage is per browser tab and dies with it; a
// different employee's answer is never reused (EmployeeId check).
import { useSyncExternalStore } from "react";
import { fetchSubscriptionFlag, getEmployeeId } from "../api/subscriptionApi";

const UNPAID_RECHECK_MS = 60 * 1000;
const FAILED_RETRY_MS = 60 * 1000;
const STORAGE_KEY = "subscriptionFlagCache";

// sessionStorage can be blocked (private mode / policies) — the cache is
// only a saving, so any storage error just means "call the API".
const readStoredCache = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    return stored?.flag && stored.employeeId && stored.freshUntil ? stored : null;
  } catch {
    return null;
  }
};

const writeStoredCache = (cache) => {
  try {
    if (cache) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore — next page load just calls the API
  }
};

const storedCache = readStoredCache();

let snapshot = { flag: storedCache?.flag ?? null, loading: false, error: null };
let inFlight = null;
// Who the cached answer belongs to, and until when it may be reused.
let cachedFor = storedCache?.employeeId ?? null;
let freshUntil = storedCache?.freshUntil ?? 0;
const listeners = new Set();

const setSnapshot = (patch) => {
  snapshot = { ...snapshot, ...patch };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => snapshot;

const nextMidnight = () => {
  const date = new Date();
  date.setHours(24, 0, 0, 0);
  return date.getTime();
};

const freshUntilFor = (flag) => {
  if (!flag) return Date.now() + FAILED_RETRY_MS;
  if (flag.SubscriptionExpired) return Date.now() + UNPAID_RECHECK_MS;
  return nextMidnight();
};

// Concurrent callers (guard + a screen mounting at the same moment) share
// one request rather than racing two.
export const refreshSubscriptionFlag = () => {
  if (inFlight) return inFlight;

  const employeeId = getEmployeeId();
  setSnapshot({ loading: true });
  inFlight = fetchSubscriptionFlag()
    .then((res) => {
      const flag = res?.success && res.data ? res.data : null;
      cachedFor = employeeId;
      freshUntil = freshUntilFor(flag);
      // Only real answers survive a refresh; a failed check is retried.
      writeStoredCache(flag ? { employeeId, freshUntil, flag } : null);
      if (flag) {
        setSnapshot({ flag, loading: false, error: null });
        return flag;
      }
      // Fail-open: if the server can't tell us, never lock the RM out.
      setSnapshot({ flag: null, loading: false, error: res?.message || "CHECK_FAILED" });
      return null;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

// Route-change check: no API call while the cached answer is still fresh
// for the logged-in employee.
export const ensureSubscriptionFlag = () => {
  const isFresh = cachedFor === getEmployeeId() && Date.now() < freshUntil;
  return isFresh ? Promise.resolve(snapshot.flag) : refreshSubscriptionFlag();
};

// After pay-now / mandate approval the money only lands via the bank
// callback, ~1–2 minutes later. Re-check at ~60s and ~120s (guide S3) and
// let the caller refresh its own data (ledger) on the same ticks.
export const scheduleSubscriptionRefresh = (onTick, delaysMs = [60 * 1000, 120 * 1000]) => {
  const timers = delaysMs.map((delay) =>
    setTimeout(() => {
      refreshSubscriptionFlag();
      onTick?.();
    }, delay)
  );
  return () => timers.forEach(clearTimeout);
};

// Called on login, so the first page after it always checks fresh.
export const clearSubscriptionFlag = () => {
  cachedFor = null;
  freshUntil = 0;
  writeStoredCache(null);
  setSnapshot({ flag: null, error: null });
};

export const useSubscriptionFlag = () => useSyncExternalStore(subscribe, getSnapshot);
